'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { type LocalizedStringFormatter, useLocalizedStringFormatter } from 'react-aria';
import { I18nProvider as RACI18nProvider, useLocale as useRACLocale } from 'react-aria-components';
import { enUS, type LocalizedMessages, type LocalizedString } from './messages/index.js';

/** Defines the shape of an object containing localized strings. */
export type Messages = Record<string, Record<string, LocalizedString>>;
/** A utility type that makes every property optional within an object containing localized strings. */
export type PartialMessages<T extends Messages> = { [P in keyof T]?: Partial<T[P]> };

const LocalizedMessagesContext = createContext<Messages>(enUS);

/** Contains information about the current locale. */
export type LocaleInfo = ReturnType<typeof useRACLocale>;

/**
 * Returns the current locale and layout direction.
 *
 * See [localization guide](https://ui.cimpress.io/dev-guides/localization/).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useLocaleInfo(): LocaleInfo {
  return useRACLocale();
}

/**
 * Creates type-safe tools for localizing custom components.
 *
 * See [localization guide](https://ui.cimpress.io/dev-guides/localization/).
 */
export function UNSTABLE_createLocalizationTools<T extends Messages = LocalizedMessages>() {
  function LocalizationProvider({
    children,
    locale: newLocale,
    messages: newMessages,
  }: {
    /** The component tree that will be localized (usually the entire application). */
    children: ReactNode;
    /** The locale to apply to the children. Should be a [BCP47](https://www.ietf.org/rfc/bcp/bcp47.txt) language code. */
    locale?: string;
    /** The localized messages for use by the components within this component tree. */
    messages?: PartialMessages<T>;
  }) {
    const messages = useContext(LocalizedMessagesContext) as T;
    const { locale } = useLocaleInfo();

    const patchedMessages: T = useMemo(() => patchMessages(messages, newMessages), [messages, newMessages]);

    return (
      <LocalizedMessagesContext.Provider value={patchedMessages}>
        <RACI18nProvider locale={newLocale ?? locale}>{children}</RACI18nProvider>
      </LocalizedMessagesContext.Provider>
    );
  }

  LocalizationProvider.displayName = 'LocalizationProvider';

  function useLocalizedMessages<R extends keyof T>(
    /** The key under which the messages are grouped, usually the component name. */
    key: R,
  ): LocalizedStringFormatter<
    // @ts-expect-error `keyof` infers `string | number | symbol` even though this interface only has string keys
    keyof T[R],
    string
  > {
    const { locale } = useLocaleInfo();
    const messages = useContext(LocalizedMessagesContext) as T;

    return useLocalizedStringFormatter<
      // @ts-expect-error `keyof` infers `string | number | symbol` even though this interface only has string keys
      keyof T[R],
      string
    >(
      {
        // `useLocalizedStringFormatter` expects its argument to contain messages for a single component, grouped by locale
        [locale]: messages[key],
      },
      key as string,
    );
  }

  return {
    /**
     * Provides localization data to Cimpress UI components.
     *
     * See [localization guide](https://ui.cimpress.io/dev-guides/localization/).
     */
    LocalizationProvider,
    /**
     * Returns a localized message formatter for a specific component.
     *
     * See [localization guide](https://ui.cimpress.io/dev-guides/localization/).
     */
    useLocalizedMessages,
  };
}

// eslint-disable-next-line react-refresh/only-export-components
export const { LocalizationProvider, useLocalizedMessages } = UNSTABLE_createLocalizationTools();

/** Patch object `a` with properties from object `b` while ignoring undefined values. */
function patchMessages<T extends Messages>(a: T, b?: PartialMessages<T>): T {
  if (!b || Object.values(b).every((value) => value === undefined)) {
    return a;
  }

  const result: T = { ...a };

  for (const [componentKey, componentMessages] of Object.entries(b)) {
    if (!componentMessages || Object.values(componentMessages).every((value) => value === undefined)) {
      continue;
    }

    const castComponentKey = componentKey as keyof T;

    result[castComponentKey] = { ...result[castComponentKey] };

    for (const [messageKey, message] of Object.entries(componentMessages)) {
      if (message) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (result[castComponentKey] as any)[messageKey] = message;
      }
    }
  }

  return result;
}
